import { useState } from "react";
import { UploadCloud, Loader2, Gauge, FileDown, ShieldAlert } from "lucide-react";
import { memberApi } from "../../lib/memberApi";
import { formatApiErrorDetail } from "../../lib/api";

const initialProfile = {
  Organization_Type: "",
  Eligibility_Notes: "",
  Technical_Capability: "",
  Past_Performance: "",
};

const DISCLOSURE_TEXT = "PHMEG provides this system as a drafting, scoring, and decision-support aid only. We make no promises or claims that using this system will guarantee eligibility, responsiveness, shortlisting, funding, contract award, or any other procurement outcome.";

export function BidReadinessScorer() {
  const [file, setFile] = useState(null);
  const [profile, setProfile] = useState(initialProfile);
  const [procurementType, setProcurementType] = useState("grant");
  const [solicitationLevel, setSolicitationLevel] = useState("federal");
  const [applicationRequirements, setApplicationRequirements] = useState("");
  const [acceptedDisclosure, setAcceptedDisclosure] = useState(false);
  const [scoring, setScoring] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const setField = (k, v) => setProfile((p) => ({ ...p, [k]: v }));

  const handleAnalyze = async (e) => {
    e.preventDefault();
    if (!file) {
      setError("Please upload the RFP/NOFO PDF first.");
      return;
    }
    if (!acceptedDisclosure) {
      setError("You must accept the disclosure and release statement before scoring.");
      return;
    }

    setScoring(true);
    setError("");
    setResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("client_profile", JSON.stringify(profile));
      formData.append("procurement_type", procurementType);
      formData.append("solicitation_level", solicitationLevel);
      formData.append("application_requirements", applicationRequirements);
      formData.append("disclosure_accepted", acceptedDisclosure ? "true" : "false");
      const { data } = await memberApi.post("/member/scorer/analyze", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not score this RFP. Please try again.");
    } finally {
      setScoring(false);
    }
  };

  return (
    <div data-testid="bid-scorer-tool">
      <form onSubmit={handleAnalyze} className="space-y-5 mb-8">
        <div>
          <label className="overline mb-3 block">Select opportunity type</label>
          <div className="flex flex-wrap gap-3">
            {[
              { id: "grant", label: "Grant" },
              { id: "contract", label: "Contract" },
            ].map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => setProcurementType(option.id)}
                className={`px-4 py-2 border rounded-md text-sm transition ${procurementType === option.id ? "border-[var(--red)] text-white bg-[var(--surface)]" : "border-[var(--border)] text-[var(--text-2)] hover:text-white"}`}
                data-testid={`scorer-procurement-${option.id}`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="overline mb-3 block">Solicitation level</label>
          <div className="flex flex-wrap gap-3">
            {[
              { id: "local", label: "Local" },
              { id: "state", label: "State" },
              { id: "federal", label: "Federal" },
            ].map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => setSolicitationLevel(option.id)}
                className={`px-4 py-2 border rounded-md text-sm transition ${solicitationLevel === option.id ? "border-[var(--red)] text-white bg-[var(--surface)]" : "border-[var(--border)] text-[var(--text-2)] hover:text-white"}`}
                data-testid={`scorer-level-${option.id}`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="overline mb-2 block">Upload solicitation PDF</label>
          <label className="card-dark p-6 flex items-center gap-3 cursor-pointer hover:bg-white/5 transition-colors" data-testid="scorer-upload-dropzone">
            <UploadCloud className="w-5 h-5 text-[var(--red)]" />
            <span className="text-sm text-[var(--text-2)]">{file ? file.name : "Click to choose a PDF file"}</span>
            <input type="file" accept=".pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="scorer-file-input" />
          </label>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="overline mb-2 block">Organization Type</label>
            <input value={profile.Organization_Type} onChange={(e) => setField("Organization_Type", e.target.value)} className="input-dark" placeholder="e.g. 501(c)(3) nonprofit" data-testid="scorer-org-type" />
          </div>
          <div>
            <label className="overline mb-2 block">Eligibility Notes</label>
            <input value={profile.Eligibility_Notes} onChange={(e) => setField("Eligibility_Notes", e.target.value)} className="input-dark" placeholder="Certifications, service area…" data-testid="scorer-eligibility" />
          </div>
        </div>

        <div>
          <label className="overline mb-2 block">Technical Capability</label>
          <textarea value={profile.Technical_Capability} onChange={(e) => setField("Technical_Capability", e.target.value)} className="input-dark" rows={3} placeholder="Program design, staffing, methodology…" data-testid="scorer-technical" />
        </div>

        <div>
          <label className="overline mb-2 block">Past Performance</label>
          <textarea value={profile.Past_Performance} onChange={(e) => setField("Past_Performance", e.target.value)} className="input-dark" rows={3} placeholder="Similar past awards, outcomes…" data-testid="scorer-past-performance" />
        </div>

        <div>
          <label className="overline mb-2 block">Known submission requirements (optional)</label>
          <textarea value={applicationRequirements} onChange={(e) => setApplicationRequirements(e.target.value)} className="input-dark" rows={3} placeholder="Required attachments, forms, page limits, certifications…" data-testid="scorer-application-requirements" />
        </div>

        <div className="card-dark p-5 border border-[var(--border)]" data-testid="scorer-disclosure-block">
          <div className="flex items-start gap-3">
            <ShieldAlert className="w-5 h-5 text-[var(--red)] mt-0.5 shrink-0" />
            <div>
              <div className="overline mb-2">Disclosure & release of liability</div>
              <p className="text-sm text-[var(--text-2)] leading-relaxed" data-testid="scorer-disclosure-copy">{DISCLOSURE_TEXT}</p>
            </div>
          </div>
          <label className="flex items-start gap-3 mt-4 text-sm text-[var(--text-2)]" data-testid="scorer-disclosure-checkbox-wrapper">
            <input type="checkbox" checked={acceptedDisclosure} onChange={(e) => setAcceptedDisclosure(e.target.checked)} data-testid="scorer-disclosure-checkbox" />
            <span>I understand this tool supports drafting and scoring only, and does not guarantee any award outcome.</span>
          </label>
        </div>

        {error && <div className="text-sm text-[var(--red)]" data-testid="scorer-error">{error}</div>}

        <button type="submit" disabled={scoring} className="btn-red" data-testid="scorer-analyze-btn">
          {scoring ? (<><Loader2 className="w-4 h-4 animate-spin" /> Analyzing…</>) : (<><Gauge className="w-4 h-4" /> Score This Bid</>)}
        </button>
      </form>

      {result && <ScoreReport result={result} />}
    </div>
  );
}

function ScoreCard({ label, score, rationale }) {
  return (
    <div className="card-dark p-5">
      <div className="text-xs uppercase tracking-wide text-[var(--silver)] mb-2">{label}</div>
      <div className="font-display text-3xl font-semibold mb-2">{score}<span className="text-base text-[var(--text-3)]">/10</span></div>
      <p className="text-xs text-[var(--text-2)]">{rationale}</p>
    </div>
  );
}

function ScoreReport({ result }) {
  const downloadSummary = async () => {
    if (!result?.summary_document?.id) return;
    const response = await memberApi.get(`/member/documents/${result.summary_document.id}`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([response.data], { type: result.summary_document.mime_type }));
    const link = document.createElement("a");
    link.href = url;
    link.download = result.summary_document.filename;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="border-t border-[var(--border)] pt-8" data-testid="scorer-result">
      <div className="flex items-baseline gap-3 mb-6">
        <div className="overline text-[var(--red)]">Overall Score</div>
        <div className="font-display text-3xl font-semibold" data-testid="scorer-overall-score">{result.overall_score}/10</div>
      </div>

      <div className="card-dark p-5 mb-6" data-testid="scorer-disclosure-result-block">
        <div className="overline text-[var(--silver)] mb-2">Disclosure</div>
        <p className="text-sm text-[var(--text-2)] leading-relaxed">{result.disclosure_statement || DISCLOSURE_TEXT}</p>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        <ScoreCard label="Eligibility" score={result.eligibility_score} rationale={result.eligibility_rationale} />
        <ScoreCard label="Technical Capability" score={result.technical_capability_score} rationale={result.technical_capability_rationale} />
        <ScoreCard label="Past Performance" score={result.past_performance_score} rationale={result.past_performance_rationale} />
      </div>

      <div className="card-dark p-6 mb-6">
        <div className="overline text-[var(--silver)] mb-2">Recommendation</div>
        <p className="text-sm text-[var(--text-2)] leading-relaxed">{result.recommendation}</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <div className="overline text-[var(--silver)] mb-2">Key Strengths</div>
          <ul className="space-y-1 text-sm text-[var(--text-2)]">
            {(result.key_strengths || []).map((s) => <li key={s}>+ {s}</li>)}
          </ul>
        </div>
        <div>
          <div className="overline text-[var(--silver)] mb-2">Key Gaps</div>
          <ul className="space-y-1 text-sm text-[var(--text-2)]">
            {(result.key_gaps || []).map((g) => <li key={g}>− {g}</li>)}
          </ul>
        </div>
      </div>

      {result.summary_document && (
        <button onClick={downloadSummary} className="btn-ghost mt-6" data-testid="scorer-download-summary-btn">
          <FileDown className="w-4 h-4" /> Download summary document
        </button>
      )}
    </div>
  );
}